import { useEffect, useMemo, useRef, useState, type JSX } from 'react';

import { Permission } from '@bookora/shared';
import {
  Alert,
  Button,
  Drawer,
  Icon,
  Num,
  Progress,
  SkeletonLines,
  Switch,
  Tag,
  Typography,
  findAnchor,
  useConfirm,
} from '@bookora/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/token-store';
import { SupportDialog } from '@/components/support-contact';
import { listPatients } from '@/features/patients/api';
import { toListParams } from '@/features/patients/hooks';
import { DEFAULT_PATIENT_FILTERS, patientKeys } from '@/features/patients/keys';
import { toast } from '@/lib/feedback';
import { useCan } from '@/permissions/use-can';

import { useGuidance, useRecordGuidance, useResetGuidance, useUpdateGuidanceProfile } from './api';
import { useStepContext, useViewport } from './context';
import { tourStatus, type TourStatus } from './eligibility';
import { useTourFeatures } from './features';
import { useHubProbeStore, useHubSections, type HubSectionDef } from './hub-sections';
import { resolveSteps, tourAllowed } from './resolve-steps';
import { startTour } from './runtime';
import { RUNTIME_COPY } from './runtime-copy';
import { SETUP_CHECKLIST_PATH, useSetupProgress } from './setup-progress';
import { findItem } from './state';
import { clearLastOffer, clearTabGuard, writeCoreSeen } from './storage';
import { useTourStore } from './tour-store';
import { COPY } from './tours/copy';
import { TOURS, TOUR_ORDER, recommendedTours } from './tours/registry';
import type { TourDef } from './tours/types';

/** A row's status tag; a tour closed part-way (`skipped`) carries none — it simply starts afresh. */
const TAG: Record<TourStatus, { label: string; color?: string } | null> = {
  new: { label: COPY.hub.tags.new },
  declined: { label: COPY.hub.tags.new },
  retired: { label: COPY.hub.tags.new },
  'in-progress': { label: COPY.hub.tags.inProgress, color: 'warning' },
  skipped: null,
  done: { label: COPY.hub.tags.done, color: 'success' },
  updated: { label: COPY.hub.tags.updated, color: 'processing' },
};

/**
 * «راهنما و پیشنهادها» — the one hub. Registered sections first (the smart
 * guide's «پیشنهادها», order 10), then «شروع کار» (the core tour, the setup
 * checklist for whoever the dashboard card is for, and the tours recommended
 * for the viewer's role), then «راهنمای بخش‌ها», then the viewer's own
 * preferences.
 *
 * Starting a tour from here closes the hub first and starts after its exit:
 * the hub is a modal dialog, and would block the tour's first step.
 */
/**
 * A touch screen, where the kit's long-press is how a control's name is read
 * (there is no hover). The hub says so once, in a static line.
 */
function isCoarsePointer(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(pointer: coarse)').matches ?? false;
}

export function GuideHub(): JSX.Element {
  const open = useTourStore((s) => s.hubOpen);
  const focus = useTourStore((s) => s.hubFocus);
  const source = useTourStore((s) => s.hubSource);
  const closeHub = useTourStore((s) => s.closeHub);
  const runAfterHubClose = useTourStore((s) => s.runAfterHubClose);
  const pathname = useTourStore((s) => s.pathname);
  const viewport = useViewport();
  const touch = useMemo(isCoarsePointer, []);
  const record = useRecordGuidance();
  const sections = useHubSections();
  const visible = useHubProbeStore((s) => s.visible);
  const bodyRef = useRef<HTMLDivElement>(null);

  // HUB_OPENED, once per opening.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      record({ type: 'HUB_OPENED', kind: 'TOUR', key: 'hub', version: 1, source });
    }
    wasOpen.current = open;
  }, [open, record, source]);

  // A page change closes the hub (nothing it shows belongs to the old page's moment).
  const openedOn = useRef(pathname);
  useEffect(() => {
    if (open) openedOn.current = pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (open && pathname !== openedOn.current) closeHub();
  }, [pathname, open, closeHub]);

  // A focused section (a tip deep link) is scrolled into view once open.
  useEffect(() => {
    const section = focus?.section;
    if (!open || !section) return;
    const timer = setTimeout(() => {
      bodyRef.current
        ?.querySelector(`[data-hub-section="${section}"]`)
        ?.scrollIntoView?.({ block: 'start' });
    }, 50);
    return () => clearTimeout(timer);
  }, [open, focus]);

  const renderSection = (def: HubSectionDef): JSX.Element | null =>
    visible[def.key] === true ? (
      <section
        key={def.key}
        className="bk-hub__section"
        data-hub-section={def.key}
        aria-label={def.title}
      >
        <Typography.Title level={4} className="bk-hub__heading">
          {def.title}
        </Typography.Title>
        <def.Component focusId={focus?.section === def.key ? (focus.id ?? null) : null} />
      </section>
    ) : null;

  return (
    <Drawer
      open={open}
      placement="end"
      width={viewport === 'compact' ? '100%' : 420}
      title={COPY.hub.title}
      onClose={() => closeHub()}
      afterOpenChange={(isOpen) => {
        if (!isOpen) runAfterHubClose();
      }}
      className="bk-hub"
      destroyOnHidden
    >
      <div ref={bodyRef} className="bk-hub__body">
        <Typography.Paragraph type="secondary" className="bk-hub__intro">
          {COPY.hub.intro}
        </Typography.Paragraph>
        {touch ? (
          <Typography.Paragraph type="secondary" className="bk-hub__touch-hint">
            {COPY.hub.touchHint}
          </Typography.Paragraph>
        ) : null}
        {sections.filter((s) => s.order < 100).map(renderSection)}
        <GettingStarted />
        <SectionTours />
        {sections.filter((s) => s.order >= 100).map(renderSection)}
        <HubFooter />
      </div>
    </Drawer>
  );
}

function helpLauncher(): HTMLElement | null {
  return findAnchor('shell.help') ?? findAnchor('shell.user-menu');
}

function useTourRows(): {
  loading: boolean;
  error: boolean;
  retry: () => void;
  statusOf: (tour: TourDef) => TourStatus | null;
  lastStepOf: (tour: TourDef) => number | null;
} {
  const guidance = useGuidance();
  const data = guidance.data;
  return {
    loading: guidance.isLoading,
    error: guidance.isError,
    retry: () => void guidance.refetch(),
    statusOf: (tour) =>
      data ? tourStatus(findItem(data, 'TOUR', tour.key), tour, new Date()) : null,
    lastStepOf: (tour) => findItem(data, 'TOUR', tour.key)?.lastStep ?? null,
  };
}

function TourRow({
  tour,
  status,
  lastStep,
}: {
  tour: TourDef;
  status: TourStatus | null;
  lastStep: number | null;
}): JSX.Element {
  const pathname = useTourStore((s) => s.pathname);
  const search = useTourStore((s) => s.search);
  const context = tour.route?.context;
  const here = tour.route?.match(pathname, search) ?? false;
  const contextual = context !== undefined && !here;
  const noPatient = useNoPatientReason(contextual && context === 'patient');

  let action: JSX.Element;
  if (contextual) {
    action = (
      <Button
        size="small"
        disabledReason={noPatient}
        onClick={() => startTour(tour.key, { source: 'hub' })}
      >
        {COPY.hub.context[context].button}
      </Button>
    );
  } else {
    const resume = status === 'in-progress';
    const again = status === 'done';
    action = (
      <Button
        size="small"
        type={status === 'new' || status === 'updated' ? 'primary' : 'default'}
        onClick={() =>
          startTour(tour.key, {
            source: 'hub',
            resumeIndex: resume ? (lastStep ?? 0) : undefined,
            returnFocusTo: helpLauncher(),
          })
        }
      >
        {resume ? COPY.hub.buttons.resume : again ? COPY.hub.buttons.again : COPY.hub.buttons.show}
      </Button>
    );
  }

  return (
    <li className="bk-hub__row">
      <div className="bk-hub__row-text">
        <div className="bk-hub__row-title">
          <span>{tour.title}</span>
          {status && TAG[status] ? <Tag color={TAG[status].color}>{TAG[status].label}</Tag> : null}
        </div>
        <Typography.Text type="secondary" className="bk-hub__row-desc">
          {tour.hubDescription}
        </Typography.Text>
        {contextual ? (
          <Typography.Text type="secondary" className="bk-hub__row-helper">
            {COPY.hub.context[context].helper}
          </Typography.Text>
        ) : null}
      </div>
      <div className="bk-hub__row-action">{action}</div>
    </li>
  );
}

/**
 * The patient-file tour starts from a patient's file: with no patient to open,
 * «رفتن به بیماران» would dead-end on an empty list. Asked only when that row
 * is contextual here, and only of `patient.view` holders (one row, cached).
 */
function useNoPatientReason(enabled: boolean): string | null {
  const canView = useCan(Permission.PATIENT_VIEW);
  const canCreate = useCan(Permission.PATIENT_CREATE);
  // Without `patient.view_all` the list is the viewer's OWN patients: an empty
  // one says nothing about the clinic, so it must not say «none registered».
  const seesAll = useCan(Permission.PATIENT_VIEW_ALL);
  const { activeClinicId } = useAuth();
  const ask = enabled && canView && Boolean(activeClinicId);
  const query = useQuery({
    queryKey: patientKeys.list(activeClinicId, DEFAULT_PATIENT_FILTERS, 1, 1),
    queryFn: ({ signal }) => listPatients(toListParams(DEFAULT_PATIENT_FILTERS, 1, 1), signal),
    enabled: ask,
    staleTime: 60_000,
  });
  if (!ask || !query.data || query.data.total > 0) return null;
  return canCreate && seesAll ? RUNTIME_COPY.hub.noPatients : RUNTIME_COPY.hub.noPatientsViewOnly;
}

/**
 * The recommended tours this viewer can actually take, in order: the tour
 * content's one list (`tours/registry.ts`), which the core tour's «بعدی: …»
 * finish reads too.
 */
function useRecommended(): TourDef[] {
  const ctx = useStepContext();
  const features = useTourFeatures();
  return recommendedTours(ctx, features).map((key) => TOURS[key]);
}

function GettingStarted(): JSX.Element {
  const rows = useTourRows();
  const setup = useSetupProgress();
  const recommended = useRecommended();
  const navigate = useNavigate();
  const closeHub = useTourStore((s) => s.closeHub);
  const go = (to: string): void => closeHub(() => void navigate(to));
  const recommendedDone = recommended.filter((tour) => rows.statusOf(tour) === 'done').length;

  return (
    <section
      className="bk-hub__section"
      data-hub-section="getting-started"
      aria-label={COPY.hub.gettingStarted}
    >
      <Typography.Title level={4} className="bk-hub__heading">
        {COPY.hub.gettingStarted}
      </Typography.Title>
      <ul className="bk-hub__list">
        <TourRow
          tour={TOURS.core}
          status={rows.statusOf(TOURS.core)}
          lastStep={rows.lastStepOf(TOURS.core)}
        />
        {setup.visible ? (
          <li className="bk-hub__row">
            <div className="bk-hub__row-text">
              <div className="bk-hub__row-title">{COPY.hub.setupTitle}</div>
              <Typography.Text type="secondary" className="bk-hub__row-desc">
                <Num value={setup.completed} />
                {COPY.hub.setupProgressJoin}
                <Num value={setup.total} />
                {COPY.hub.setupProgressTail}
              </Typography.Text>
              {setup.next ? (
                <Typography.Text className="bk-hub__row-desc">
                  {RUNTIME_COPY.hub.setupNext(setup.next.title)}
                </Typography.Text>
              ) : null}
              <Progress
                className="bk-hub__progress"
                percent={setup.percent}
                showInfo={false}
                size="small"
                aria-label={COPY.hub.setupTitle}
              />
            </div>
            <div className="bk-hub__row-action bk-hub__row-links">
              <Button size="small" type="primary" onClick={() => go(setup.nextPath)}>
                {COPY.hub.setupCta}
              </Button>
              <Button size="small" type="link" onClick={() => go(SETUP_CHECKLIST_PATH)}>
                {RUNTIME_COPY.hub.setupAll}
              </Button>
            </div>
          </li>
        ) : null}
      </ul>
      {recommended.length > 0 ? (
        <>
          <Typography.Text type="secondary" className="bk-hub__row-desc">
            <Num value={recommendedDone} /> {RUNTIME_COPY.hub.of} <Num value={recommended.length} />{' '}
            {RUNTIME_COPY.hub.recommendedTail} {RUNTIME_COPY.hub.recommendedSeen}
          </Typography.Text>
          <Progress
            className="bk-hub__progress"
            percent={Math.round((recommendedDone / recommended.length) * 100)}
            showInfo={false}
            size="small"
            aria-label={RUNTIME_COPY.hub.recommendedTail}
          />
          <ul className="bk-hub__list">
            {recommended.map((tour) => (
              <TourRow
                key={tour.key}
                tour={tour}
                status={rows.statusOf(tour)}
                lastStep={rows.lastStepOf(tour)}
              />
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}

function SectionTours(): JSX.Element {
  const rows = useTourRows();
  const ctx = useStepContext();
  const features = useTourFeatures();
  // Those already listed under «شروع کار» are not repeated here.
  const recommended = new Set(useRecommended().map((tour) => tour.key));
  const tours = TOUR_ORDER.map((key) => TOURS[key]).filter(
    (tour) =>
      !recommended.has(tour.key) &&
      tourAllowed(tour, ctx.permissions, features) &&
      resolveSteps(tour, ctx, { trim: false }).length >= tour.minSteps,
  );

  return (
    <section
      className="bk-hub__section"
      data-hub-section="section-tours"
      aria-label={COPY.hub.sections}
    >
      <Typography.Title level={4} className="bk-hub__heading">
        {COPY.hub.sections}
      </Typography.Title>
      {rows.error ? (
        <Alert
          type="warning"
          showIcon={false}
          className="bk-hub__alert"
          message={COPY.hub.loadError}
          action={
            <Button size="small" onClick={rows.retry}>
              {COPY.hub.retry}
            </Button>
          }
        />
      ) : null}
      {rows.loading ? (
        <SkeletonLines lines={4} />
      ) : (
        <ul className="bk-hub__list">
          {tours.map((tour) => (
            <TourRow
              key={tour.key}
              tour={tour}
              status={rows.statusOf(tour)}
              lastStep={rows.lastStepOf(tour)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function HubFooter(): JSX.Element {
  const guidance = useGuidance();
  const { impersonatedBy, user } = useAuth();
  const update = useUpdateGuidanceProfile();
  const reset = useResetGuidance();
  const confirm = useConfirm();
  const setWelcome = useTourStore((s) => s.setWelcome);
  const [supportOpen, setSupportOpen] = useState(false);
  const readOnly = Boolean(impersonatedBy) || guidance.data?.readOnly === true;
  const profile = guidance.data?.profile;

  const onReset = async (): Promise<void> => {
    const ok = await confirm({
      title: COPY.hub.resetConfirmTitle,
      content: COPY.hub.resetConfirmContent,
      okText: COPY.hub.resetOk,
      cancelText: COPY.hub.resetCancel,
    });
    if (!ok) return;
    reset.mutate(undefined, {
      onSuccess: () => {
        writeCoreSeen(user?.id, false);
        clearTabGuard();
        clearLastOffer();
        // «From the start» means the welcome, from the next page on.
        setWelcome('unknown');
        toast.success(COPY.hub.resetDone);
      },
      onError: (error) => toast.error(error),
    });
  };

  return (
    <footer className="bk-hub__footer">
      <label className="bk-hub__switch">
        <Switch
          checked={profile?.autoOfferTours ?? true}
          disabled={readOnly || !profile}
          onChange={(checked) => update.mutate({ autoOfferTours: checked })}
          aria-describedby="bk-hub-switch-help"
        />
        <span>{RUNTIME_COPY.hub.autoOfferSwitch}</span>
      </label>
      <Typography.Text type="secondary" id="bk-hub-switch-help" className="bk-hub__helper">
        {RUNTIME_COPY.hub.autoOfferHelper}
      </Typography.Text>
      <div className="bk-hub__footer-actions">
        <Button
          icon={<Icon name="refresh" />}
          disabledReason={readOnly ? RUNTIME_COPY.hub.resetReadOnlyReason : null}
          // The profile still arriving is work in flight, not a reason.
          loading={reset.isPending || !profile}
          onClick={() => void onReset()}
        >
          {COPY.hub.reset}
        </Button>
        <Button type="text" icon={<Icon name="chat" />} onClick={() => setSupportOpen(true)}>
          {COPY.hub.support}
        </Button>
      </div>
      {readOnly ? (
        <Typography.Text type="secondary" className="bk-hub__helper">
          {COPY.hub.impersonating}
        </Typography.Text>
      ) : null}
      <SupportDialog open={supportOpen} onClose={() => setSupportOpen(false)} />
    </footer>
  );
}
